import type { SxProps, Theme } from "@mui/material";
import { Card, CardMedia } from "@mui/material";
import type { Ref } from "react";
import { useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import usePageViewTracking from "./pdfViewer/usePageViewTracking";

interface Props {
  src: string;
  poster?: string;
  documentViewSessionId?: string;
  videoRef?: Ref<HTMLVideoElement>;
  sx?: SxProps<Theme>;
}

interface TrackingState {
  pauseTracking: boolean;
  currentSegmentNumber: number;
  numSegments: number;
  videoFileDurationSec: number;
}

const initialTrackingState: TrackingState = {
  pauseTracking: true,
  currentSegmentNumber: 0,
  numSegments: 0,
  videoFileDurationSec: 0,
};

const VideoPlayer = ({ src, poster, documentViewSessionId, videoRef, sx }: Props) => {
  const [trackingState, setTrackingState] = useState<TrackingState>(initialTrackingState);

  usePageViewTracking({
    sessionId: documentViewSessionId,
    numPages: trackingState.numSegments,
    videoFileDurationSec: trackingState.videoFileDurationSec,
    visiblePages: trackingState.currentSegmentNumber > 0 ? [trackingState.currentSegmentNumber] : [],
    pauseTracking: trackingState.pauseTracking,
  });

  const handleChangePlaying = useDebounce((newPlaying: boolean) => {
    if (!documentViewSessionId) {
      return;
    }

    setTrackingState((prev) => ({ ...prev, pauseTracking: !newPlaying }));
  }, 1000);

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!documentViewSessionId) {
      return;
    }

    const duration = e.currentTarget.duration;

    setTrackingState((prev) => ({
      ...prev,
      videoFileDurationSec: Math.round(duration),
      numSegments: Math.min(Math.ceil(duration / 10), 20), // 10 seconds per segment, but max. 20 segments
    }));
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!documentViewSessionId || trackingState.numSegments === 0 || e.currentTarget.duration === 0) {
      return;
    }

    const currentSegmentNum = Math.ceil(
      (e.currentTarget.currentTime / e.currentTarget.duration) * trackingState.numSegments
    );

    if (currentSegmentNum > 0 && currentSegmentNum !== trackingState.currentSegmentNumber) {
      setTrackingState((prev) => ({ ...prev, currentSegmentNumber: currentSegmentNum }));
    }
  };

  return (
    <Card sx={sx} variant="outlined">
      <CardMedia
        ref={videoRef}
        component="video"
        controls
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        src={src}
        poster={poster}
        onPlay={() => handleChangePlaying(true)}
        onPause={() => handleChangePlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        sx={{ objectFit: "contain", border: 0, height: "100%", width: "100%" }}
      />
    </Card>
  );
};

export default VideoPlayer;
